<template>
    <div class="settings-page">
        <div class="d-flex align-items-center justify-content-between mb-4">
            <div>
                <h2 class="m-0">{{ t('settings') }}</h2>
                <div class="text-muted small">{{ t('settings_subtitle') }}</div>
            </div>
            <div class="d-flex gap-2">
                <button class="btn btn-outline-secondary" type="button" @click="reload" :disabled="loading">
                    {{ t('reload') }}
                </button>
                <button class="btn btn-primary" type="button" @click="saveAll" :disabled="saving || loading">
                    {{ t('save_all') }}
                </button>
            </div>
        </div>

        <div class="row g-3">
            <div class="col-12 col-lg-3">
                <div class="card">
                    <div class="card-body p-2">
                        <button
                            v-for="section in displaySections"
                            :key="section.key"
                            type="button"
                            class="list-group-item list-group-item-action w-100 text-start settings-tab"
                            :class="{ active: activeKey === section.key }"
                            @click="activeKey = section.key"
                        >
                            {{ section.title }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-9">
                <div class="card">
                    <div class="card-body">
                        <div v-if="loading" class="text-muted">{{ t('loading') }}</div>
                        <div v-else-if="!isLanguageSection && !activeSection" class="text-muted">{{ t('no_settings_section_found') }}</div>

                        <div v-else-if="isLanguageSection" class="row g-3">
                            <div class="col-12">
                                <h5 class="m-0">{{ t('language') }}</h5>
                                <div class="text-muted small">{{ t('language_subtitle') }}</div>
                            </div>
                            <div class="col-12">
                                <div class="border rounded p-3 d-flex align-items-center justify-content-between">
                                    <div class="fw-semibold">{{ t('language') }}</div>
                                    <div class="lang-toggle">
                                        <button type="button" class="lang-btn" :class="{ active: currentLang === 'en' }" @click="setLanguage('en')">ENG</button>
                                        <button type="button" class="lang-btn" :class="{ active: currentLang === 'bn' }" @click="setLanguage('bn')">বাংলা</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form v-else class="row g-3" @submit.prevent="saveActive">
                            <div class="col-12">
                                <h5 class="m-0">{{ activeSection.title }}</h5>
                                <div class="text-muted small">{{ t('configure') }} {{ activeSection.title }}</div>
                            </div>

                            <div
                                v-for="field in activeSection.fields || []"
                                :key="field.key"
                                class="col-12"
                            >
                                <div v-if="field.type === 'toggle'" class="d-flex align-items-center justify-content-between border rounded p-3">
                                    <div>
                                        <div class="fw-semibold">{{ field.label }}</div>
                                        <div v-if="field.help" class="text-muted small">{{ field.help }}</div>
                                    </div>
                                    <div class="form-check form-switch m-0">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="`toggle-${field.key}`"
                                            v-model="form[field.key]"
                                        />
                                    </div>
                                </div>

                                <div v-else-if="field.type === 'image'">
                                    <label class="form-label" :for="`field-${field.key}`">{{ field.label }}</label>
                                    <div class="d-flex flex-column gap-2">
                                        <div v-if="form[field.key]" class="border rounded p-2 bg-light">
                                            <img :src="form[field.key]" style="max-width: 100%; max-height: 160px; object-fit: contain;">
                                        </div>
                                        <div class="d-flex gap-2 align-items-center">
                                            <input
                                                class="form-control"
                                                :id="`field-${field.key}`"
                                                type="file"
                                                accept="image/*,.ico"
                                                :disabled="saving || uploading[field.key]"
                                                @change="onPickFile(field, $event)"
                                            />
                                            <button
                                                v-if="form[field.key]"
                                                class="btn btn-outline-secondary"
                                                type="button"
                                                :disabled="saving || uploading[field.key]"
                                                @click="clearImage(field.key)"
                                            >
                                                {{ t('clear') }}
                                            </button>
                                        </div>
                                        <div v-if="uploading[field.key]" class="text-muted small">{{ t('loading') }}</div>
                                    </div>
                                </div>

                                <div v-else>
                                    <label class="form-label" :for="`field-${field.key}`">{{ field.label }}</label>

                                    <select
                                        v-if="field.type === 'select'"
                                        class="form-select"
                                        :id="`field-${field.key}`"
                                        v-model="form[field.key]"
                                    >
                                        <option v-for="opt in field.options || []" :key="opt.value" :value="opt.value">
                                            {{ opt.label }}
                                        </option>
                                    </select>

                                    <textarea
                                        v-else-if="field.type === 'textarea'"
                                        class="form-control"
                                        :id="`field-${field.key}`"
                                        rows="3"
                                        v-model="form[field.key]"
                                    ></textarea>

                                    <input
                                        v-else
                                        class="form-control"
                                        :id="`field-${field.key}`"
                                        :type="inputType(field)"
                                        v-model="form[field.key]"
                                        :placeholder="passwordPlaceholder(field)"
                                    />

                                    <div v-if="field.type === 'password' && passwordHasValue[field.key]" class="text-muted small mt-1">
                                        Saved. Leave empty to keep unchanged.
                                    </div>
                                </div>
                            </div>

                            <div class="col-12 d-flex justify-content-end gap-2">
                                <button class="btn btn-outline-secondary" type="button" @click="resetActive" :disabled="saving">
                                    {{ t('reset') }}
                                </button>
                                <button class="btn btn-primary" type="submit" :disabled="saving">
                                    {{ t('save') }} {{ activeSection.title }}
                                </button>
                            </div>

                            <div v-if="message" class="col-12">
                                <div class="alert" :class="messageType === 'success' ? 'alert-success' : 'alert-danger'" role="alert">
                                    {{ message }}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, inject, onMounted, reactive, ref } from 'vue';
import axios from 'axios';

export default {
    name: 'SettingsPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));
        const adminSetLang = inject('adminSetLang', (value) => {
            const l = value === 'bn' ? 'bn' : 'en';
            localStorage.setItem('admin_lang', l);
        });

        const loading = ref(true);
        const saving = ref(false);
        const sections = ref([]);
        const activeKey = ref('');
        const message = ref('');
        const messageType = ref('success');

        const form = reactive({});
        const initial = ref({});
        const passwordHasValue = reactive({});
        const uploading = reactive({});

        const activeSection = computed(() => {
            return sections.value.find(s => s.key === activeKey.value) || null;
        });

        const t = (key) => adminT(key);
        const isLanguageSection = computed(() => activeKey.value === '__language');
        const currentLang = computed(() => (adminLang?.value === 'bn' ? 'bn' : 'en'));
        const setLanguage = (value) => {
            adminSetLang(value);
        };

        const displaySections = computed(() => {
            const list = Array.isArray(sections.value) ? sections.value.slice() : [];
            list.push({ key: '__language', title: t('language') });
            return list;
        });

        const normalizeDefault = (field) => {
            if (field.type === 'toggle') return !!field.default;
            if (field.type === 'number') return field.default ?? '';
            return field.default ?? '';
        };

        const applyPayload = (payload) => {
            sections.value = Array.isArray(payload.sections) ? payload.sections : [];
            if (!activeKey.value && sections.value.length > 0) {
                activeKey.value = sections.value[0].key;
            }

            const values = payload.values || {};
            const nextInitial = {};

            sections.value.forEach(section => {
                (section.fields || []).forEach(field => {
                    const key = field.key;
                    const valueFromApi = Object.prototype.hasOwnProperty.call(values, key) ? values[key] : normalizeDefault(field);
                    form[key] = valueFromApi;
                    nextInitial[key] = valueFromApi;
                });
            });

            initial.value = nextInitial;

            const meta = payload.meta || {};
            const passMeta = meta.password_has_value || {};
            Object.keys(passwordHasValue).forEach(k => delete passwordHasValue[k]);
            Object.keys(passMeta).forEach(k => {
                passwordHasValue[k] = !!passMeta[k];
            });
        };

        const reload = async () => {
            loading.value = true;
            message.value = '';
            try {
                const res = await axios.get('/admin/api/settings');
                applyPayload(res.data || {});
            } catch (e) {
                messageType.value = 'error';
                message.value = e?.response?.data?.message || t('failed_to_load_settings');
            } finally {
                loading.value = false;
            }
        };

        const resetActive = () => {
            if (!activeSection.value) return;
            (activeSection.value.fields || []).forEach(field => {
                const key = field.key;
                form[key] = initial.value[key];
            });
            message.value = '';
        };

        const saveActive = async () => {
            if (!activeSection.value) return;
            saving.value = true;
            message.value = '';

            const values = {};
            (activeSection.value.fields || []).forEach(field => {
                values[field.key] = form[field.key];
            });

            try {
                await axios.put('/admin/api/settings', { values });
                messageType.value = 'success';
                message.value = t('settings_saved');
                await reload();
            } catch (e) {
                messageType.value = 'error';
                const data = e?.response?.data;
                if (data?.errors) {
                    const firstKey = Object.keys(data.errors)[0];
                    message.value = data.errors[firstKey]?.[0] || t('validation_failed');
                } else {
                    message.value = data?.message || t('failed_to_save_settings');
                }
            } finally {
                saving.value = false;
            }
        };

        const saveAll = async () => {
            saving.value = true;
            message.value = '';

            const values = {};
            sections.value.forEach(section => {
                (section.fields || []).forEach(field => {
                    values[field.key] = form[field.key];
                });
            });

            try {
                await axios.put('/admin/api/settings', { values });
                messageType.value = 'success';
                message.value = t('settings_saved');
                await reload();
            } catch (e) {
                messageType.value = 'error';
                const data = e?.response?.data;
                if (data?.errors) {
                    const firstKey = Object.keys(data.errors)[0];
                    message.value = data.errors[firstKey]?.[0] || t('validation_failed');
                } else {
                    message.value = data?.message || t('failed_to_save_settings');
                }
            } finally {
                saving.value = false;
            }
        };

        const inputType = (field) => {
            if (field.type === 'password') return 'password';
            if (field.type === 'number') return 'number';
            if (field.type === 'email') return 'email';
            if (field.type === 'url') return 'url';
            return 'text';
        };

        const passwordPlaceholder = (field) => {
            if (field.type !== 'password') return '';
            return passwordHasValue[field.key] ? '••••••••' : '';
        };

        const onPickFile = async (field, event) => {
            const file = event?.target?.files?.[0];
            if (!file || !field?.key) return;
            await uploadImage(field.key, file);
            if (event?.target) {
                event.target.value = '';
            }
        };

        const uploadImage = async (key, file) => {
            uploading[key] = true;
            message.value = '';
            try {
                const fd = new FormData();
                fd.append('key', key);
                fd.append('file', file);
                const res = await axios.post('/admin/api/settings/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const url = res?.data?.url || '';
                form[key] = url;
                initial.value = { ...initial.value, [key]: url };
                messageType.value = 'success';
                message.value = t('settings_saved');
            } catch (e) {
                messageType.value = 'error';
                const data = e?.response?.data;
                if (data?.errors) {
                    const firstKey = Object.keys(data.errors)[0];
                    message.value = data.errors[firstKey]?.[0] || t('validation_failed');
                } else {
                    message.value = data?.message || t('failed_to_save_settings');
                }
            } finally {
                uploading[key] = false;
            }
        };

        const clearImage = (key) => {
            form[key] = '';
            message.value = '';
        };

        onMounted(reload);

        return {
            t,
            loading,
            saving,
            sections,
            displaySections,
            activeKey,
            activeSection,
            isLanguageSection,
            currentLang,
            setLanguage,
            form,
            passwordHasValue,
            uploading,
            message,
            messageType,
            reload,
            resetActive,
            saveActive,
            saveAll,
            inputType,
            passwordPlaceholder,
            onPickFile,
            clearImage,
        };
    },
};
</script>

<style scoped>
.lang-toggle {
    display: flex;
    gap: 10px;
}

.lang-btn {
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    border: 1px solid rgba(15, 23, 42, 0.14);
    color: rgba(15, 23, 42, 0.88);
    background: rgba(15, 23, 42, 0.04);
}

.lang-btn.active {
    background: rgba(15, 23, 42, 0.12);
    border-color: rgba(15, 23, 42, 0.22);
    color: #0f172a;
}

.settings-tab {
    border: 0;
    border-radius: 10px;
    padding: 12px 14px;
    margin: 4px 0;
}

.settings-tab.active {
    background: #0d6efd;
    color: #ffffff;
}
</style>
